import React from 'react';
import cx from 'classnames';

import Skeleton from '../../../Skeleton';
import Box from '../../../Box';
import Text from '../../../Text';
import Checkmark20 from '../../Icon/library/Checkmark20';
import AlertWarning16 from '../../Icon/library/AlertWarning16';
import Information16 from '../../Icon/library/Information16';
import Search18 from '../../Icon/library/Search18';

import styles from './TextInput.module.scss';
import useUniqueId from '../../../utils/useUniqueId';

export interface InputProps {
  /** The autoCapitalize attribute for the input. */
  autoCapitalize?: string;
  /** The autoComplete attribute for the input. */
  autoComplete?: string;
  /** The autoCorrect attribute for the input. */
  autoCorrect?: string;
  /** When true, the input will automatically receive focus when the component mounts. */
  autoFocus?: boolean;
  /** Apply className to the component's container. */
  className?: Parameters<typeof cx>[0];
  /**
   * If true, the success indicator will be visible.
   * @default false
   */
  disabled?: boolean;
  /**
   * If false, the label will be hidden visually.
   * @default true
   */
  hasLabel?: boolean;
  /** The id attribute for the input. */
  id: string;
  /** A description or hint for the input. */
  infoContent?: React.ReactNode;
  /** Content to be displayed when the input is in an invalid state. */
  invalidContent?: React.ReactNode;
  /**
   * If true, the succuss indicator will be visible.
   * @default false
   */
  isFulfilled?: boolean;
  /**
   * If true, will apply invalid styles.
   * @default false
   */
  isInValid?: boolean;
  /**
   * When true, the loading indicator will be visible.
   * @default false
   */
  isLoading?: boolean;
  /** The label for the input. */
  label: string;
  /** Callback function called when the input value changes. */
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Callback function called when the input loses focus. */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** The name of the input that is used as the key when the form is submitted. */
  name?: string;
  /** Specify the input type. Can be 'text', 'email', 'number', 'search', or 'tel'. */
  type?: 'text' | 'email' | 'number' | 'search' | 'tel' | 'password';
  /** The maximum length of the input value. */
  maxLength?: number;
  /** The minimum length of the input value. */
  minLength?: number;
  /** Callback function called when the input receives focus. */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** Callback function called when a key is pressed down while the input has focus. */
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Callback function called when a key is pressed while the input has focus. */
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /** A regular expression pattern for input validation. */
  pattern?: string;
  /**
   * When true, the input is in read-only mode and cannot be edited.
   * @default false
   */
  readOnly?: boolean;
  /** A reference to the input element. */
  ref?: any;
  /**
   * When true, the input is required and must have a value.
   * @default false
   */
  required?: boolean;
  /** The current value of the input. */
  value: string;
}

export const BaseInput: React.FC<InputProps> = ({
  autoCapitalize,
  autoComplete,
  autoCorrect,
  autoFocus,
  className,
  disabled,
  hasLabel,
  id,
  infoContent,
  invalidContent,
  isFulfilled,
  isInValid,
  isLoading,
  label,
  onChange,
  onBlur,
  name,
  type = 'text',
  maxLength,
  minLength,
  onFocus,
  onKeyDown,
  onKeyPress,
  pattern,
  readOnly,
  ref,
  required,
  value,
}) => {
  const isSearch = type === 'search';
  const infoContentId = useUniqueId('info-content');
  const invalidContentId = useUniqueId('invalid-content');

  return (
    <>
      <div
        className={cx(
          styles.baseInputRoot,
          isSearch && styles.search,
          isInValid && styles.isInValid,
          disabled && styles.isDisabled,
          isLoading && styles.isLoading,
          isFulfilled && styles.isFulfilled,
          className,
        )}
      >
        {(isLoading || isFulfilled) && (
          <Box
            display='flex'
            alignItems='center'
            gap={isFulfilled ? 1 : 0}
            className={cx(
              styles.skeletonContainer,
              isLoading || isFulfilled
                ? styles.loadingIsVisible
                : styles.loadingIsHidden,
            )}
          >
            <Box
              display='flex'
              className={cx(
                styles.checkmarkContainer,
                isFulfilled ? styles.slideFadeIn : styles.slideFadeOut,
              )}
            >
              <Checkmark20 />
            </Box>
            <Skeleton effect='sheen' className={styles.skeleton} />
          </Box>
        )}

        <Box
          display='flex'
          alignItems={isSearch ? 'center' : undefined}
          gap={isSearch ? 1 : undefined}
        >
          {isSearch && <Search18 />}
          <input
            className={cx(
              styles.input,
              !isLoading && !isFulfilled
                ? styles.loadingIsVisible
                : styles.loadingIsHidden,
            )}
            placeholder={label}
            aria-describedby={
              isInValid
                ? invalidContentId
                : infoContent
                ? infoContentId
                : undefined
            }
            {...{
              autoCapitalize,
              autoComplete,
              autoCorrect,
              autoFocus,
              id,
              maxLength,
              minLength,
              name,
              onBlur,
              onChange,
              onFocus,
              onKeyDown,
              onKeyPress,
              pattern,
              readOnly,
              ref,
              required,
              type,
              value,
            }}
            disabled={disabled || isLoading || isFulfilled}
            aria-invalid={isInValid}
          />

          <div
            className={cx(
              styles.labelContainer,
              (isLoading || isFulfilled || hasLabel || isSearch) &&
                styles.isHidden,
            )}
          >
            <label className={styles.label} htmlFor={id} aria-hidden={true}>
              {label}
            </label>
          </div>
        </Box>
      </div>
      {!isInValid && infoContent && (
        <Box
          mt={1}
          display='grid'
          gridTemplateColumns='auto 1fr'
          gap={1}
          px={2}
        >
          <Box>
            <Information16 />
          </Box>
          {typeof infoContent === 'string' ? (
            <Text
              size={12}
              m={0}
              className={styles.supplementaryText}
              id={infoContentId}
              align='start'
            >
              {infoContent}
            </Text>
          ) : (
            <Box
              className={styles.supplementaryContentContainer}
              id={infoContentId}
            >
              {infoContent}
            </Box>
          )}
        </Box>
      )}
      {isInValid && invalidContent && (
        <Box
          mt={1}
          display='grid'
          gridTemplateColumns='auto 1fr'
          gap={1}
          px={2}
        >
          <Box>
            <AlertWarning16 className={styles.invalidIcon} />
          </Box>
          {typeof invalidContent === 'string' ? (
            <Text
              size={12}
              m={0}
              className={styles.supplementaryText}
              id={invalidContentId}
              align='start'
            >
              {invalidContent}
            </Text>
          ) : (
            <Box
              className={styles.supplementaryContentContainer}
              id={invalidContentId}
            >
              {invalidContent}
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

BaseInput.displayName = 'BaseInput';
export default BaseInput;
export { styles };
